import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Accordion from 'components/common/Accordion';
import { ThinkingIndicator } from 'components/common';
import { colours } from 'utils/theme';
import {
  setNLPModalState,
  setPendingSearchView,
  setRecentSearches,
} from 'actions/searchNLP';
import { getSearchView } from 'selectors/search';
import { getNLPModalState, getShowNLPFilterModal } from 'selectors/searchNLP';
import {
  aiRecentSearches,
  aiSearchHistoryPin,
  aiSearchHistoryDelete,
} from 'services/api';
import { view } from 'components/Search/Results/constants';
import {
  ResearcherSearch,
  ActivitySearch,
} from 'components/Search/Results/helpers';
import { SuggestionList } from './NLPSuggestionList';
import { NLPOrgGuidanceQueries } from './NLPOrgGuidanceQueries';
import { RecentSearchItem } from './RecentSearchItem';
import {
  SuggestionsGrid,
  SuggestionSection,
  StageOneRecentAndExamplesWrap,
  SectionTitle,
  ErrorIcon,
  SciSearchLimitBanner,
  SpellCheckBox,
  SpellCheckMessage,
  SpellCheckSuggestionButton,
  PreCheckBoxStack,
  EmptyState,
  PillsContainer,
  PinnedSection,
  PinnedSeparator,
  PinnedWatermark,
  RecentSearchesScrollArea,
} from './styled';
import { NLPExpandingSearchInput } from './NLPExpandingSearchInput';
import { EntityDisambiguationChooser } from './EntityDisambiguationChooser';
import {
  getDefaultSciSearchDailyLimitMessage,
  getDefaultSciSearchRateLimitMessage,
} from './useAiSearchCredits';
import { NLPAPIErrorBox } from './NLPAPIErrorBox';
import { NLPOrgRedirectView } from './NLPOrgRedirectView';
import { orgViewForEntityType } from './NlpOrgContext';
import {
  stageOneIntro,
  stageOnePlaceholder,
  orgStageOnePlaceholder,
} from './strings';

const normalizeSuggestionItems = suggestionResponse => {
  const searches = suggestionResponse?.searches;
  if (!Array.isArray(searches)) {
    return [];
  }

  return searches
    .map((searchItem, index) => {
      if (typeof searchItem === 'string') {
        return {
          id: `legacy-${index}`,
          prompt: searchItem,
          viewName: null,
          category: null,
          sortOrder: index,
        };
      }

      if (!searchItem || typeof searchItem !== 'object') {
        return null;
      }

      const prompt = searchItem.prompt || searchItem.Prompt;
      if (!prompt) {
        return null;
      }

      return {
        id: searchItem.id || searchItem.Id || `${prompt}-${index}`,
        prompt,
        viewName: searchItem.viewName ?? searchItem.ViewName ?? null,
        category: searchItem.category ?? searchItem.Category ?? null,
        savedFilters:
          searchItem.savedFilters ?? searchItem.SavedFilters ?? null,
        isPinned: searchItem.isPinned ?? searchItem.IsPinned ?? false,
        sortOrder: Number.isFinite(searchItem.sortOrder)
          ? searchItem.sortOrder
          : Number.isFinite(searchItem.SortOrder)
          ? searchItem.SortOrder
          : index,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.sortOrder - right.sortOrder);
};

export const toCanonicalView = viewName => {
  const normalizedViewName =
    typeof viewName === 'string' ? viewName.toLowerCase().trim() : '';

  if (!normalizedViewName) {
    return null;
  }

  if (
    normalizedViewName === 'researcher' ||
    normalizedViewName === 'research' ||
    normalizedViewName === 'researchersearch' ||
    normalizedViewName === 'researchers'
  ) {
    return view.researcher;
  }

  if (
    normalizedViewName === 'organisation' ||
    normalizedViewName === 'organization' ||
    normalizedViewName === 'company' ||
    normalizedViewName === 'organisationsearch' ||
    normalizedViewName === 'organizationsearch' ||
    normalizedViewName === 'organisations'
  ) {
    return view.organisation;
  }

  if (normalizedViewName === 'map' || normalizedViewName === 'researchermap') {
    return view.map;
  }

  if (
    normalizedViewName === 'organisationmap' ||
    normalizedViewName === 'organizationmap'
  ) {
    return view.organisationMap;
  }

  if (
    normalizedViewName === 'visualisation' ||
    normalizedViewName === 'visualization'
  ) {
    return view.visualisation;
  }

  if (
    normalizedViewName === 'organisationinsights' ||
    normalizedViewName === 'organizationinsights'
  ) {
    return view.organisationInsights;
  }

  if (
    normalizedViewName === 'activity' ||
    normalizedViewName === 'activities' ||
    normalizedViewName === 'activitysearch'
  ) {
    return view.activity;
  }

  if (normalizedViewName.includes('research')) {
    return view.researcher;
  }

  if (
    normalizedViewName.includes('organisation') ||
    normalizedViewName.includes('organization') ||
    normalizedViewName.includes('company')
  ) {
    return view.organisation;
  }

  if (normalizedViewName.includes('activity')) {
    return view.activity;
  }

  return null;
};

const getRecentSearchIcon = suggestionItem => {
  const resolvedView = toCanonicalView(suggestionItem?.viewName);
  switch (resolvedView) {
    case view.researcher:
    case view.visualisation:
      return 'user';
    case view.organisation:
    case view.organisationInsights:
      return 'company';
    case view.activity:
      // Work-item/activity icon scoped to this dialog — not the global stethoscope.
      return 'fileLines';
    case view.map:
    case view.organisationMap:
      return 'map';
    default:
      return suggestionItem?.viewName ? 'magnifyingGlassNormal' : null;
  }
};

/** Maps portal search tab to API `viewName` values on example rows (recents are user-global; row `viewName` drives icons). */
export const toApiViewName = currentSearchView => {
  switch (currentSearchView) {
    case view.organisation:
    case view.organisationInsights:
    case view.organisationMap:
      return 'organisation';
    case view.activity:
      return 'activity';
    case view.researcher:
    case view.visualisation:
    case view.map:
    default:
      return 'researcher';
  }
};

const MAX_PINNED_RECENT_SEARCHES = 5;

/** Pinned: newest pin first (matches API LastUsedAtUtc). Unpin: item at top of recent. */
const reorderAfterPinToggle = (previous, toggledId, nowPinned) => {
  const withToggle = previous.map(existing =>
    existing.id === toggledId ? { ...existing, isPinned: nowPinned } : existing
  );
  const target = withToggle.find(existing => existing.id === toggledId);
  if (!target) {
    return previous;
  }
  const others = withToggle.filter(existing => existing.id !== toggledId);
  const otherPinned = others.filter(existing => existing.isPinned);
  const nonPinned = others.filter(existing => !existing.isPinned);
  if (nowPinned) {
    return [target, ...otherPinned, ...nonPinned];
  }
  return [...otherPinned, target, ...nonPinned];
};

/**
 * Stage-one content. GET state for recent + example searches lives in NLPSearchModal
 * so it is not reset when this view unmounts on navigation to stage 2.
 */
export function NLPStageOneModalView({
  loading,
  onSearchButtonClick,
  onMainSearchRedirect,
  recentData,
  recentLoading,
  recentError,
  setRecentListResponse,
  suggestionsData,
  isOutOfCredits = false,
  isDemo = false,
  recentSearchesCollapsed,
  onRecentSearchesCollapsedChange,
  orgCtx = null,
}) {
  const dispatch = useDispatch();
  // On the org profile recents come from the org-scoped endpoint.
  const isOrgContext = Boolean(orgCtx);
  const recentsUrl = orgCtx?.recentSearchesUrl ?? aiRecentSearches;
  const showModal = useSelector(getShowNLPFilterModal);
  const mainSearchView = useSelector(getSearchView);
  // The view the modal starts from: the profile tab on an org page, the URL view
  // on main search. pendingSearchView resets to this whenever the query changes.
  const currentSearchView = orgCtx
    ? orgViewForEntityType(orgCtx.entityType)
    : mainSearchView;
  const nlpModalWasOpen = useRef(false);
  const {
    query,
    apiError,
    orgMainSearchRedirect,
    spellCheckPending,
    correctedQuery,
    ambiguityPending,
    pendingSuggestedTypes,
  } = useSelector(getNLPModalState);

  // Guard: never offer a chip for the view the user is already on. The backend already
  // excludes the current view, but if entityType drifts out of sync (e.g. a stale request)
  // the chooser could otherwise suggest the current tab, which is meaningless and was the
  // source of the "change context to Contacts" bug while already on Contacts.
  const currentCanonicalView = ResearcherSearch(currentSearchView)
    ? view.researcher
    : ActivitySearch(currentSearchView)
    ? view.activity
    : view.organisation;
  const safeSuggestedTypes = useMemo(
    () =>
      (pendingSuggestedTypes || []).filter(
        entityType => toCanonicalView(entityType) !== currentCanonicalView
      ),
    [pendingSuggestedTypes, currentCanonicalView]
  );

  const [displayRecentSearches, setDisplayRecentSearches] = useState([]);
  const listRefreshDebounceRef = useRef(null);
  const listRefreshEpochRef = useRef(0);

  const LIST_REFRESH_DEBOUNCE_MS = 1000;

  useEffect(() => {
    if (showModal && !nlpModalWasOpen.current) {
      dispatch(setPendingSearchView(currentSearchView));
    }
    nlpModalWasOpen.current = showModal;
  }, [dispatch, showModal, currentSearchView]);

  const recentSearches = useMemo(() => normalizeSuggestionItems(recentData), [
    recentData,
  ]);
  const exampleSearches = useMemo(
    () =>
      normalizeSuggestionItems({
        searches:
          suggestionsData?.exampleSearches ?? suggestionsData?.ExampleSearches,
      }),
    [suggestionsData]
  );
  const advancedUseCases = useMemo(
    () =>
      normalizeSuggestionItems({
        searches:
          suggestionsData?.advancedUseCases ??
          suggestionsData?.AdvancedUseCases,
      }),
    [suggestionsData]
  );

  const setQuery = nextQuery => {
    // Any query change invalidates prior pre-check resolutions — the spellcheck
    // box and the chooser should both clear, and both agents should get a chance
    // to re-evaluate. Also reset pendingSearchView back to the URL view: a pill
    // choice from a previous query shouldn't bleed into a new query (it'd lock
    // entityType to the old chosen view).
    dispatch(
      setNLPModalState({
        query: nextQuery,
        orgMainSearchRedirect: false,
        savedFilters: null,
        skipSpellCheck: false,
        skipClassification: false,
        spellCheckPending: false,
        correctedQuery: '',
        spellCheckOriginalQuery: '',
        ambiguityPending: false,
        pendingSuggestedTypes: null,
      })
    );
    dispatch(setPendingSearchView(currentSearchView));
  };

  const handleUseSuggestedPrompt = () => {
    // Accepting the correction updates the query text — spellcheck box hides.
    // Stage-1 happens once: skip both spellcheck and classify on the next call so
    // the user isn't surprised by a fresh chooser/spellcheck banner after they
    // thought they were done. Pills computed against the typo'd query stay
    // visible (typos rarely change classification) and remain clickable. The
    // chooser dispatch already sets skipClassification on its own; we mirror
    // that here so both stage-1 paths converge on the same standard.
    dispatch(
      setNLPModalState({
        query: correctedQuery,
        spellCheckPending: false,
        correctedQuery: '',
        spellCheckOriginalQuery: '',
        skipSpellCheck: true,
        skipClassification: true,
      })
    );
  };

  const handleSuggestionClick = suggestionItem => {
    // Example searches are curated with a known entity type — no need to re-run
    // spellcheck or the classifier on them. Skip both.
    // Only mirror the prompt into the input: Search must resolve examples from the
    // live box text so edits before "Search" are not ignored (no selectedExampleId).
    dispatch(
      setNLPModalState({
        query: suggestionItem?.prompt || '',
        orgMainSearchRedirect: false,
        savedFilters: null,
        skipSpellCheck: true,
        skipClassification: true,
        ambiguityPending: false,
        pendingSuggestedTypes: null,
      })
    );
  };

  const handleRecentSearchClick = suggestionItem => {
    // SCIL-15352: clicking a recent should behave like typing the prompt fresh — let
    // the classifier run so the chooser can reappear if the prompt is ambiguous, and
    // so the saved row's ViewName is updated to whatever the user picks this time.
    // Spellcheck is still skipped: the prompt was vetted on first run.
    dispatch(
      setNLPModalState({
        query: suggestionItem?.prompt || '',
        orgMainSearchRedirect: false,
        savedFilters: null,
        skipSpellCheck: true,
        skipClassification: false,
        ambiguityPending: false,
        pendingSuggestedTypes: null,
      })
    );
    dispatch(setPendingSearchView(toCanonicalView(suggestionItem?.viewName)));
  };

  useLayoutEffect(() => {
    // Keep the currently displayed list while a refresh is pending.
    // Only replace it when the server has returned a concrete searches payload.
    const hasSearchesPayload =
      Array.isArray(recentData?.searches) ||
      Array.isArray(recentData?.Searches);

    if (!hasSearchesPayload) {
      return;
    }

    setDisplayRecentSearches(recentSearches);
  }, [recentData, recentSearches]);

  const applyRecentListFromServer = useCallback(
    (normalized, epochWhenRefreshStarted, rawApiResponse) => {
      if (listRefreshEpochRef.current !== epochWhenRefreshStarted) {
        return;
      }
      setDisplayRecentSearches(normalized);
      dispatch(setRecentSearches(normalized));
      if (rawApiResponse != null && setRecentListResponse) {
        setRecentListResponse(rawApiResponse);
      }
    },
    [dispatch, setRecentListResponse]
  );

  const fetchRecentListForEpoch = useCallback(
    async epochWhenRefreshStarted => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(recentsUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        const normalized = normalizeSuggestionItems(data);
        applyRecentListFromServer(normalized, epochWhenRefreshStarted, data);
      } catch {
        // best-effort refresh
      }
    },
    [applyRecentListFromServer, recentsUrl]
  );

  const scheduleDebouncedRecentListRefresh = useCallback(() => {
    listRefreshEpochRef.current += 1;
    const epochWhenTimerFires = listRefreshEpochRef.current;
    if (listRefreshDebounceRef.current) {
      clearTimeout(listRefreshDebounceRef.current);
    }
    listRefreshDebounceRef.current = setTimeout(() => {
      listRefreshDebounceRef.current = null;
      void fetchRecentListForEpoch(epochWhenTimerFires);
    }, LIST_REFRESH_DEBOUNCE_MS);
  }, [fetchRecentListForEpoch]);

  useEffect(
    () => () => {
      if (listRefreshDebounceRef.current) {
        clearTimeout(listRefreshDebounceRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!showModal && listRefreshDebounceRef.current) {
      clearTimeout(listRefreshDebounceRef.current);
      listRefreshDebounceRef.current = null;
    }
  }, [showModal]);

  const fireMutationThenScheduleListRefresh = useCallback(
    (url, options) => {
      const token = localStorage.getItem('token');
      void fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
      }).catch(() => {});
      scheduleDebouncedRecentListRefresh();
    },
    [scheduleDebouncedRecentListRefresh]
  );

  const handlePinToggle = useCallback(
    item => {
      const nowPinned = !item.isPinned;
      if (nowPinned) {
        const pinnedCount = displayRecentSearches.filter(
          existing => existing.isPinned
        ).length;
        if (pinnedCount >= MAX_PINNED_RECENT_SEARCHES) {
          return;
        }
      }
      setDisplayRecentSearches(previous =>
        reorderAfterPinToggle(previous, item.id, nowPinned)
      );
      fireMutationThenScheduleListRefresh(aiSearchHistoryPin(item.id), {
        method: 'PATCH',
        body: JSON.stringify({ isPinned: nowPinned }),
      });
    },
    [displayRecentSearches, fireMutationThenScheduleListRefresh]
  );

  const handleDeleteItem = useCallback(
    item => {
      setDisplayRecentSearches(previous =>
        previous.filter(existing => existing.id !== item.id)
      );
      fireMutationThenScheduleListRefresh(aiSearchHistoryDelete(item.id), {
        method: 'DELETE',
      });
    },
    [fireMutationThenScheduleListRefresh]
  );

  const pinnedItems = useMemo(
    () => displayRecentSearches.filter(item => item.isPinned),
    [displayRecentSearches]
  );
  const nonPinnedItems = useMemo(
    () => displayRecentSearches.filter(item => !item.isPinned),
    [displayRecentSearches]
  );
  const pinLimitReached = pinnedItems.length >= MAX_PINNED_RECENT_SEARCHES;

  useEffect(() => {
    if (!showModal) return;
    dispatch(setRecentSearches(displayRecentSearches));
  }, [dispatch, showModal, displayRecentSearches]);

  const isDailyLimitApiError = apiError?.errorCode === 'daily_limit_reached';
  const isRateLimitApiError = apiError?.errorCode === 'rate_limit_reached';
  const showSciSearchLimitBanner =
    isOutOfCredits || isDailyLimitApiError || isRateLimitApiError;
  const defaultLimitMessage = getDefaultSciSearchDailyLimitMessage(isDemo);
  const defaultRateLimitMessage = getDefaultSciSearchRateLimitMessage();
  const sciSearchLimitText = isDailyLimitApiError
    ? apiError.explanation || defaultLimitMessage
    : isRateLimitApiError
    ? apiError.explanation || defaultRateLimitMessage
    : isOutOfCredits
    ? defaultLimitMessage
    : null;

  // Stage one is the only place the org scope is stated before a query runs.
  const organisationName = orgCtx?.organisationName?.trim() ?? '';
  const showOrgScope = organisationName !== '';
  const placeholder = showOrgScope
    ? orgStageOnePlaceholder(organisationName)
    : stageOnePlaceholder;

  return (
    <>
      <p
        data-testid='nlp-stage-one-intro'
        style={{ marginBottom: '16px', color: '#595959' }}
      >
        {showOrgScope ? (
          <>
            Describe who or what you're looking for within{' '}
            <strong>{organisationName}</strong> in plain language.
          </>
        ) : (
          stageOneIntro
        )}
      </p>

      {/* When out of credits, avoid passing the stored query as value (HTML inputs only
          show placeholder when value is empty). Limit messaging is in SciSearchLimitBanner. */}
      <NLPExpandingSearchInput
        value={isOutOfCredits ? '' : query}
        onChange={setQuery}
        loading={loading}
        onSearchButtonClick={onSearchButtonClick}
        disabled={isOutOfCredits}
        placeholder={placeholder}
      />

      {loading && <ThinkingIndicator style={{ marginTop: 8 }} />}

      {showSciSearchLimitBanner && sciSearchLimitText && (
        <SciSearchLimitBanner
          data-testid='nlp-scisearch-daily-limit-banner'
          role='alert'
        >
          <ErrorIcon icon='invalid' size='sm' color={colours.red} />
          <span>{sciSearchLimitText}</span>
        </SciSearchLimitBanner>
      )}

      {(spellCheckPending ||
        (ambiguityPending && safeSuggestedTypes.length > 0)) && (
        <PreCheckBoxStack>
          {spellCheckPending && (
            <SpellCheckBox>
              <ErrorIcon
                icon='error'
                size='sm'
                color={colours.character.warning}
              />
              <SpellCheckMessage>
                Did you mean:
                <SpellCheckSuggestionButton
                  data-testid='nlp-spellcheck-suggestion'
                  onClick={handleUseSuggestedPrompt}
                  disabled={loading}
                >
                  {correctedQuery}
                </SpellCheckSuggestionButton>
              </SpellCheckMessage>
            </SpellCheckBox>
          )}

          {ambiguityPending && safeSuggestedTypes.length > 0 && (
            <EntityDisambiguationChooser
              suggestedTypes={safeSuggestedTypes}
              onChoose={entityType => {
                const chosenView = toCanonicalView(entityType);
                dispatch(
                  setNLPModalState({
                    ambiguityPending: false,
                    pendingSuggestedTypes: null,
                    skipClassification: true,
                  })
                );
                if (chosenView) {
                  dispatch(setPendingSearchView(chosenView));
                }
                // Auto-fire only when there's nothing else to resolve. If
                // spellcheck is also pending, stage the view choice and let
                // the user resolve spellcheck (or dismiss it with Search)
                // before firing — same pattern spellcheck already uses.
                if (!spellCheckPending) {
                  onSearchButtonClick(query, chosenView);
                }
              }}
              disabled={loading}
            />
          )}
        </PreCheckBoxStack>
      )}

      {apiError && !isDailyLimitApiError && !isRateLimitApiError && (
        <NLPAPIErrorBox apiError={apiError} />
      )}

      {/* Inline so the rejected query stays editable; any query change clears the banner. */}
      {isOrgContext && orgMainSearchRedirect && (
        <NLPOrgRedirectView
          organisationName={orgCtx.organisationName}
          reason={orgMainSearchRedirect}
          onRunOnMainSearch={() => onMainSearchRedirect(query)}
        />
      )}

      <StageOneRecentAndExamplesWrap>
        <div style={{ marginTop: '16px' }}>
          <Accordion
            icon='clockRotateLeft'
            title='Recent Searches'
            testId='nlp-recent-searches'
            sectionHeader
            isCollapsed={recentSearchesCollapsed}
            onToggleCollapsed={onRecentSearchesCollapsedChange}
          >
            {displayRecentSearches.length > 0 ? (
              <RecentSearchesScrollArea data-testid='nlp-recent-searches-scroll'>
                {pinnedItems.length > 0 && (
                  <PinnedSection>
                    <PinnedWatermark>Pinned</PinnedWatermark>
                    <PillsContainer>
                      {pinnedItems.map(item => (
                        <RecentSearchItem
                          key={item.id}
                          item={item}
                          isPinned
                          disabled={loading}
                          pinDisabled={pinLimitReached}
                          getIcon={getRecentSearchIcon}
                          onSelect={handleRecentSearchClick}
                          onPin={handlePinToggle}
                          onDelete={handleDeleteItem}
                        />
                      ))}
                    </PillsContainer>
                  </PinnedSection>
                )}
                {pinnedItems.length > 0 && nonPinnedItems.length > 0 && (
                  <PinnedSeparator />
                )}
                {nonPinnedItems.length > 0 && (
                  <PillsContainer>
                    {nonPinnedItems.map(item => (
                      <RecentSearchItem
                        key={item.id}
                        item={item}
                        isPinned={false}
                        disabled={loading}
                        pinDisabled={pinLimitReached}
                        getIcon={getRecentSearchIcon}
                        onSelect={handleRecentSearchClick}
                        onPin={handlePinToggle}
                        onDelete={handleDeleteItem}
                      />
                    ))}
                  </PillsContainer>
                )}
              </RecentSearchesScrollArea>
            ) : recentLoading || (!recentData && !recentError) ? (
              <EmptyState>Loading recent searches…</EmptyState>
            ) : recentError ? (
              <EmptyState>Failed to load recent searches</EmptyState>
            ) : (
              <EmptyState>No Recent Searches</EmptyState>
            )}
          </Accordion>
        </div>

        {isOrgContext ? (
          // Org profile: static non-clickable guidance from strings.js instead of the
          // clickable per-view API examples.
          <NLPOrgGuidanceQueries />
        ) : (
          (exampleSearches.length > 0 || advancedUseCases.length > 0) && (
            <SuggestionsGrid>
              {exampleSearches.length > 0 && (
                <SuggestionSection>
                  <SectionTitle>Example Searches</SectionTitle>
                  <SuggestionList
                    items={exampleSearches}
                    onSelect={handleSuggestionClick}
                    disabled={loading}
                  />
                </SuggestionSection>
              )}
              {advancedUseCases.length > 0 && (
                <SuggestionSection>
                  <SectionTitle>Advanced Use Cases</SectionTitle>
                  <SuggestionList
                    items={advancedUseCases}
                    onSelect={handleSuggestionClick}
                    disabled={loading}
                  />
                </SuggestionSection>
              )}
            </SuggestionsGrid>
          )
        )}
      </StageOneRecentAndExamplesWrap>
    </>
  );
}
